import React, { useState, useEffect, useRef } from 'react';
import useChatStore from '../../stores/useChatStore';
import FlerpieMemoryPanel from './FlerpieMemoryPanel';
import FlerpieNotesTab from './FlerpieNotesTab';

const TABS = [
  { key: 'conversations', label: 'Conversations' },
  { key: 'notes', label: 'Notes' },
];

const FlerpieConversationSidebar = () => {
  const conversations = useChatStore((s) => s.conversations);
  const activeConversationId = useChatStore((s) => s.activeConversationId);
  const selectConversation = useChatStore((s) => s.selectConversation);
  const createConversation = useChatStore((s) => s.createConversation);
  const deleteConversation = useChatStore((s) => s.deleteConversation);
  const leaveConversation = useChatStore((s) => s.leaveConversation);
  const toggleSidebar = useChatStore((s) => s.toggleSidebar);
  const tab = useChatStore((s) => s.sidebarTab);
  const setSidebarTab = useChatStore((s) => s.setSidebarTab);
  const pendingNotes = useChatStore((s) => s.pendingNotes);
  const [confirmingDelete, setConfirmingDelete] = useState(null);
  const tabRefs = useRef({});
  // The notes thread is reached from the Notes tab's Reply, never listed here.
  const chats = conversations.filter((c) => c.kind !== 'notes');

  useEffect(() => {
    if (!useChatStore.getState().userProfile) useChatStore.getState().fetchProfile();
    if (useChatStore.getState().pendingNotes > 0) useChatStore.getState().setSidebarTab('notes');
  }, []);

  const onTabKeyDown = (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const i = TABS.findIndex((t) => t.key === tab);
    const next = TABS[(i + (e.key === 'ArrowRight' ? 1 : TABS.length - 1)) % TABS.length].key;
    setSidebarTab(next);
    if (tabRefs.current[next]) tabRefs.current[next].focus();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    if (diff < 86400000) return 'Today';
    if (diff < 172800000) return 'Yesterday';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleSelect = (id) => {
    selectConversation(id);
    toggleSidebar();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="absolute inset-0 z-10"
        style={{ background: 'rgba(0,0,0,0.4)' }}
        onClick={toggleSidebar}
      />

      {/* Sidebar panel */}
      <div
        className="absolute left-0 top-0 bottom-0 z-20 flex flex-col overflow-hidden"
        style={{
          width: 240,
          background: 'linear-gradient(180deg, #1a1a1a 0%, #151515 100%)',
          borderRight: '1px solid rgba(255,204,51,0.06)',
          animation: 'flerpie-sidebar-slide 0.2s ease-out',
        }}
      >
        {/* Header: the tab strip, and New while on Conversations */}
        <div className="px-2 py-2.5 flex items-center justify-between gap-2 flex-shrink-0"
             style={{ borderBottom: '1px solid rgba(255,204,51,0.04)' }}>
          <div
            role="tablist"
            aria-label="Sidebar"
            className="flex gap-0.5 p-0.5 rounded-lg"
            style={{ background: 'rgba(0,0,0,0.25)', boxShadow: 'inset 0 1px 2px rgba(0,0,0,.35)' }}
          >
            {TABS.map((t) => {
              const selected = t.key === tab;
              return (
                <button
                  key={t.key}
                  ref={(el) => { tabRefs.current[t.key] = el; }}
                  type="button"
                  role="tab"
                  id={`flerpie-tab-${t.key}`}
                  aria-selected={selected}
                  aria-controls={`flerpie-panel-${t.key}`}
                  aria-label={t.key === 'notes' && pendingNotes > 0
                    ? `Notes, ${pendingNotes} waiting`
                    : t.label}
                  tabIndex={selected ? 0 : -1}
                  onClick={() => setSidebarTab(t.key)}
                  onKeyDown={onTabKeyDown}
                  className={`np-state flex items-center gap-1 px-1.5 py-1 rounded-md text-[11px] font-medium whitespace-nowrap
                             outline-none focus-visible:ring-1 focus-visible:ring-primary/40 transition-colors duration-150
                             ${selected ? 'bg-primary/15 text-primary' : 'text-gray-500 hover:text-gray-300'}`}
                >
                  {t.label}
                  {t.key === 'notes' && pendingNotes > 0 && (
                    <span
                      aria-hidden="true"
                      className="min-w-[14px] h-[14px] px-1 rounded-full bg-primary text-[9px] font-semibold leading-none tabular-nums text-black flex items-center justify-center"
                    >
                      {pendingNotes}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {tab === 'conversations' && (
          <button
            onClick={() => { createConversation(); toggleSidebar(); }}
            className="text-[11px] px-2.5 py-1 rounded-lg transition-all duration-200 flex-shrink-0"
            style={{
              background: 'rgba(255,204,51,0.08)',
              color: '#ffcc33',
              border: '1px solid rgba(255,204,51,0.12)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,204,51,0.15)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,204,51,0.08)'; }}
          >
            + New
          </button>
          )}
        </div>

        {tab === 'notes' ? (
          <div role="tabpanel" id="flerpie-panel-notes" aria-labelledby="flerpie-tab-notes" className="flex-1 min-h-0 flex flex-col">
            <FlerpieNotesTab />
          </div>
        ) : (
        /* Conversation list */
        <div
          role="tabpanel"
          id="flerpie-panel-conversations"
          aria-labelledby="flerpie-tab-conversations"
          className="flex-1 overflow-y-auto py-1 flex flex-col"
          style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,204,51,0.1) transparent' }}
        >
          {chats.length === 0 && (
            <p className="text-[11px] text-gray-600 text-center py-8 px-4">
              No conversations yet. Start chatting!
            </p>
          )}

          {chats.map((conv) => {
            const isActive = conv.id === activeConversationId;
            const title = conv.title || 'New conversation';
            return (
              <div
                key={conv.id}
                role="button"
                tabIndex={0}
                aria-label={title}
                aria-current={isActive ? 'true' : undefined}
                className="group flex items-center gap-2 mx-1.5 my-0.5 px-2.5 py-2 rounded-lg cursor-pointer
                           transition-all duration-150 outline-none focus-visible:ring-1 focus-visible:ring-yellow-500/40"
                style={{
                  background: isActive ? 'rgba(255,204,51,0.08)' : 'transparent',
                }}
                onClick={() => handleSelect(conv.id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSelect(conv.id); } }}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
              >
                <div className="flex-1 min-w-0">
                  <p
                    className="text-xs truncate flex items-center gap-1.5"
                    style={{ color: isActive ? '#ffcc33' : '#aaa' }}
                  >
                    <span className="truncate">{title}</span>
                  </p>
                  <p className="text-[10px] text-gray-600 mt-0.5 truncate">
                    {formatDate(conv.updated_at || conv.created_at)}
                    {conv.shared && conv.with && conv.with.length > 0 && <span> · with {conv.with.join(', ')}</span>}
                  </p>
                </div>

                {/* Delete (mine) or Leave (someone else's thread), two-click confirmation. */}
                {confirmingDelete === conv.id ? (
                  <button
                    onClick={(e) => { e.stopPropagation(); (conv.mine === false ? leaveConversation : deleteConversation)(conv.id); setConfirmingDelete(null); }}
                    onBlur={() => setConfirmingDelete(null)}
                    className="text-[10px] text-red-400 font-medium px-1.5 py-0.5 rounded
                               bg-red-400/10 border border-red-400/20 flex-shrink-0 transition-all"
                    autoFocus
                  >
                    {conv.mine === false ? 'Leave?' : 'Delete?'}
                  </button>
                ) : (
                  <button
                    onClick={(e) => { e.stopPropagation(); setConfirmingDelete(conv.id); }}
                    className="opacity-0 group-hover:opacity-100 w-6 h-6 flex items-center justify-center
                               rounded text-gray-600 hover:text-red-400 transition-all duration-150 flex-shrink-0"
                    aria-label={`${conv.mine === false ? 'Leave' : 'Delete'} conversation: ${conv.title || 'New conversation'}`}
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2 2L10 10M10 2L2 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  </button>
                )}
              </div>
            );
          })}

          <FlerpieMemoryPanel />
        </div>
        )}
      </div>
    </>
  );
};

export default FlerpieConversationSidebar;
